<template>
    <div class="container-fluid bg-white vh-100 p-0">
        <ClientLogo v-if="this.$route.name != 'behalf_application'" />
        <BreadCrumb label="Credit Report Namibia" />
        <div>
            <div class="d-flex justify-content-center align-items-center min-vh-50">
                <div class="col-11 col-sm-10 col-md-8 col-lg-6 col-xl-5 p-3">
                    <!-- ID Type and ID Number in same row -->
                    <div class="form-group mt-3">
                        <div class="row">
                            <!-- ID Type -->
                            <div class="col-4 pe-2">
                                <label class="form-label fw-bold text-xxs">ID Type <span
                                        class="text-danger fw-bold">*</span></label>
                                <select id="id_type"
                                    class="form-select text-xxs rounded-small p-2 shadow-sm"
                                    v-model="id_number_type">
                                    <option value="NationalID">National ID</option>
                                    <option value="PassportNumber">Passport</option>
                                </select>
                            </div>
                            <!-- ID Number -->
                            <div class="col-8 ps-2">
                                <label for="id_number" class="form-label fw-bold text-xxs">
                                    {{ id_number_type === 'PassportNumber' ? 'Passport Number' : 'ID Number' }}
                                    <span class="text-danger fw-bold">*</span>
                                </label>
                                <input id="id_number" type="text"
                                    class="form-control text-xxs rounded-small p-2 shadow-sm"
                                    v-model="id_number"
                                    :placeholder="id_number_type === 'PassportNumber' ? 'Enter Passport Number' : 'Enter ID Number (11 digits)'"
                                    :maxlength="id_number_type === 'PassportNumber' ? 20 : 13"
                                    @input="handle_id_input">
                            </div>
                        </div>
                        <div class="d-flex flex-column col-12 col-md-9 col-lg-8 mx-auto mt-1">
                            <small class="text-danger fw-bold text-xs d-none id-message">
                                Please fill in this field
                            </small>
                            <small class="text-danger fw-bold text-xs d-none invalid-message">
                                Please enter a valid {{ id_number_type === 'PassportNumber' ? 'passport number' : 'ID number' }}
                            </small>
                        </div>
                    </div>

                    <!-- First Name and Last Name in same row -->
                    <div class="form-group mt-3">
                        <div class="row">
                            <!-- First Name -->
                            <div class="col-6 pe-2">
                                <label for="first_name" class="form-label fw-bold text-xxs">First Name <span
                                        class="text-danger fw-bold">*</span></label>
                                <input id="first_name" type="text"
                                    class="form-control text-xxs rounded-small p-2 shadow-sm"
                                    v-model="first_name" placeholder="Enter First Name" maxlength="50">
                                <small class="text-danger fw-bold text-xs d-none first-name-message">
                                    Please fill in this field
                                </small>
                            </div>
                            <!-- Last Name -->
                            <div class="col-6 ps-2">
                                <label for="last_name" class="form-label fw-bold text-xxs">Last Name <span
                                        class="text-danger fw-bold">*</span></label>
                                <input id="last_name" type="text"
                                    class="form-control text-xxs rounded-small p-2 shadow-sm"
                                    v-model="last_name" placeholder="Enter Last Name" maxlength="50">
                                <small class="text-danger fw-bold text-xs d-none last-name-message">
                                    Please fill in this field
                                </small>
                            </div>
                        </div>
                    </div>

                    <!-- Phone Number with Country Code -->
                    <div class="form-group mt-3">
                        <label for="phone_number" class="form-label fw-bold text-xxs">Phone Number <span
                                class="text-danger fw-bold">*</span></label>
                        <PhoneInput
                            ref="phoneInput"
                            v-model="phone_number"
                            input-class="form-control text-xxs rounded-small p-2 shadow-sm w-100"
                            :show-errors="true"
                            empty-message="Please fill in this field"
                            :only-countries="phoneCountries"
                        />
                    </div>

                    <!-- Reason of Inquiry -->
                    <div class="form-group mt-3">
                        <label for="reason_of_inquiry" class="form-label fw-bold text-xxs">Reason of Inquiry <span
                                class="text-danger fw-bold">*</span></label>
                        <select id="reason_of_inquiry"
                            class="form-select mx-auto col-12 col-md-9 col-lg-8 text-xxs rounded-small p-2 shadow-sm"
                            v-model="reason_of_inquiry">
                            <option v-for="reason in inquiry_reasons" :key="reason.value" :value="reason.value">
                                {{ reason.label }}
                            </option>
                        </select>
                        <div class="d-flex flex-column col-12 col-md-9 col-lg-8 mx-auto mt-1">
                            <small class="text-danger fw-bold text-xs d-none reason-message">
                                Please select a reason
                            </small>
                        </div>
                    </div>
                </div>
            </div>
            <div class="text-center">
                <button type="submit"
                    class="btn bg-green btn-sm mt-1 rounded-small shadow-sm fw-bold secondary-text-dark text-xs"
                    @click="submit_form">
                    Continue
                </button>
            </div>
        </div>
    </div>
</template>

<script>
    import { PREPOPULATE_INPUT } from "@/utils/prepopulate_input.js";
    import { MAIN_CONTROLLER } from "@/utils/main_controller.js";
    import { API_BASE_URL } from "@/config";
    import { validateToken } from '@/utils/auth';
    import BreadCrumb from '@/components/BreadCrumb/BreadCrumb.vue';
    import ClientLogo from '@/components/ClientLogo/ClientLogo.vue';
    import PhoneInput from '@/components/PhoneInput/PhoneInput.vue';
    import Swal from "sweetalert2";

    export default {
    name: 'CreditReportNamibia',
    mixins: [MAIN_CONTROLLER, PREPOPULATE_INPUT],
    components: {
        BreadCrumb,
        ClientLogo,
        PhoneInput
    },

    data: () => ({
        id_number: "",
        id_number_type: "NationalID",
        first_name: "",
        last_name: "",
        phone_number: "",
        reason_of_inquiry: "CustomerInquiry",
        loading: false,
        error: "",
        inquiry_reasons: [
            { value: "CustomerInquiry", label: "Customer Inquiry" }
        ]
    }),

    computed: {
        phoneCountries() {
            return this.$store.getters.get_branding?.phone_countries || [];
        },
        workflow_id() {
            return this.$store.getters.get_workflow_id;
        },
        full_phone_number() {
            if (this.$refs.phoneInput) {
                return this.$refs.phoneInput.getFullNumber();
            }
            return this.phone_number;
        }
    },

    methods: {
        validatePhoneNumber() {
            return this.$refs.phoneInput?.validate() ?? false;
        },
        handle_id_input(event) {
            if (this.id_number_type === 'NationalID') {
                // For National ID, only allow numbers
                this.id_number = event.target.value.replace(/[^0-9]/g, '');
            } else {
                // For Passport, allow alphanumeric
                this.id_number = event.target.value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
            }
        },
        validate_namibian_id(id_number) {
            // Namibian National ID validation
            if (this.id_number_type === 'NationalID') {
                // Accept 11 digits for Namibian ID (standard format)
                // Also allow up to 13 digits for flexibility with other formats
                if (!id_number || id_number.length < 11) {
                    return false;
                }
                const namibian_id_regex = /^\d{11,13}$/;
                return namibian_id_regex.test(id_number);
            } else {
                const passport_regex = /^[A-Z0-9]{6,20}$/;
                return passport_regex.test(id_number);
            }
        },
        show_error(selector) {
            $(selector).removeClass('d-none');
            setTimeout(() => {
                $(selector).addClass('d-none');
            }, 3000);
        },
        submit_form() {
            if (!this.id_number.trim()) {
                this.show_error('.id-message');
                return false;
            }
            if (!this.validate_namibian_id(this.id_number)) {
                this.show_error('.invalid-message');
                return false;
            }

            if (!this.first_name.trim()) {
                this.show_error('.first-name-message');
                return false;
            }

            if (!this.last_name.trim()) {
                this.show_error('.last-name-message');
                return false;
            }
            if (!this.validatePhoneNumber()) {
                return false;
            }
            if (!this.reason_of_inquiry) {
                this.show_error('.reason-message');
                return false;
            }
            this.credit_report();
        },

        credit_report() {
            const TOKEN = localStorage.getItem('token');
            const CREDIT_REPORT_NAMIBIA_API = `${API_BASE_URL}/verify_api/credit_verification/credit_report_namibia/`;

            this.loading = true;
            this.error = '';

            $.ajax({
                url: CREDIT_REPORT_NAMIBIA_API,
                method: 'POST',
                contentType: 'application/json',
                headers: {
                    Authorization: `Token ${TOKEN}`
                },
                data: JSON.stringify({
                    id_number: this.id_number,
                    id_number_type: this.id_number_type,
                    first_name: this.first_name,
                    last_name: this.last_name,
                    phone_number: this.full_phone_number,
                    reason_of_inquiry: this.reason_of_inquiry,
                    workflow_id: this.workflow_id,
                    on_behalf_application: this.$route.name === 'behalf_application' ? true : false,
                }),
                beforeSend: () => {
                    Swal.fire({
                        text: 'Loading ...',
                        allowEscapeKey: false,
                        allowOutsideClick: false,
                        didOpen: () => Swal.showLoading(),
                    });
                },
                success: () => {
                    Swal.close();
                    this.completeService();
                },
                error: (xhr) => {
                    this.loading = false;
                    let response = xhr.responseJSON;
                    if (response && response.api_error_message) {
                        Swal.fire({
                            icon: 'error',
                            title: 'Error',
                            text: response.api_error_message,
                        });
                    } else if (response && response.message) {
                        Swal.fire({
                            icon: 'error',
                            title: response.message,
                            text: response.errors,
                        });
                    } else if (response && response.status === "SubjectNotFound") {
                        if (this.$route.name === 'behalf_application') {
                            Swal.fire({
                                icon: 'error',
                                title: response.message,
                                text: response.errors,
                            });
                        }
                    } else {
                        this.loading = false;
                        this.error = 'An error occurred while retrieving customer credit report. Please try again with a valid ID number or contact support if the issue persists.';
                        Swal.fire({
                            icon: 'error',
                            title: 'Error',
                            text: this.error,
                        });
                    }
                }
            });
        }
    },

    mounted() {
        if (!validateToken(this.$router)) {
            return;
        }
        this.get_customer_data();
    }
    };
</script>